// src/components/products/ProductForm/helpers/formInitializer.js

import { INITIAL_FORM_DATA, TOUR_TYPES } from "./formConstants";
import { getProductTypeInfo, parseMultiLang, extractImageUrl, processImages } from "../../../../utils/forms/productFormHelpers";

/**
 * ✅ Initialize form data from existing product (Edit mode)
 */
export const initializeFormData = (currentData) => {
    if (!currentData) return INITIAL_FORM_DATA;

    const productType = currentData.product_type;
    const { isTransfer, isTour } = getProductTypeInfo(productType);

    // Base form data (common for all types)
    const baseFormData = {
        product_type_id: currentData.product_type_id,
        selectedProductType: productType,

        // General fields
        category_id: currentData.category_id || null,
        transportation_id: currentData.transportation_id || null,
        city_id: currentData.city_id || currentData.city?.id || null,
        service_id: currentData.service_id || null,
        title: parseMultiLang(currentData.title),
        pricing_unit_id: currentData.pricing_unit_id || null,
        price_type: parseMultiLang(currentData.price_type),
        operating_days: parseMultiLang(currentData.operating_days),
        operating_type: parseMultiLang(currentData.operating_type),
        short_description: parseMultiLang(currentData.short_description),
        summary: parseMultiLang(currentData.summary),
        inclusions: parseMultiLang(currentData.inclusions),
        exclusions: parseMultiLang(currentData.exclusions),
        cancellation_and_change_policy: parseMultiLang(
            currentData.cancellation_and_change_policy
        ),
        locations: parseMultiLang(currentData.locations),

        // Cover image
        cover_image: currentData.cover_image?.file_name || extractImageUrl(currentData.cover_image),
        cover_image_preview: currentData.cover_image?.url || extractImageUrl(currentData.cover_image),
        uploadingCoverImage: false,
        coverImageError: "",
        coverImageSuccess: "",

        // Multiple images
        images: currentData.images
            ? currentData.images.map((img) => extractImageUrl(img))
            : [],
        uploadedImages: processImages(currentData.images),

        // Rate Availability
        rate_availability_start_date: currentData.rate_availability_start_date || "",
        rate_availability_end_date: currentData.rate_availability_end_date || "",

        // Currency & rate availability
        currency_id: currentData.currency_id || null,
        rate_availability: currentData.rate_availability || null,

        // Supplier
        supplier_ids: currentData.suppliers ? currentData.suppliers.map(s => s.id) : [],
    };

    // Transfer-specific data
    if (isTransfer && currentData.transfer) {
        return initializeTransferData(baseFormData, currentData.transfer);
    }

    // Tour-specific data
    if (isTour && currentData.tour) {
        return initializeTourData(baseFormData, currentData.tour);
    }

    // Fallback
    return { ...INITIAL_FORM_DATA, ...baseFormData };
};

/**
 * ✅ Initialize Transfer-specific data
 */
const initializeTransferData = (baseFormData, transferData) => {
    return {
        ...baseFormData,
        operating_hours_start_time: transferData.operating_hours_start_time || "",
        operating_hours_end_time: transferData.operating_hours_end_time || "",
        itinerary: parseMultiLang(transferData.itinerary),
        notes: parseMultiLang(transferData.notes),

        // Set Tour fields to defaults (not used)
        package_id: null,
        type: TOUR_TYPES.DEFAULT,
        default_itinerary: { ar: "", en: "" },
        morning_operating_hours_start_time: "",
        morning_operating_hours_end_time: "",
        evening_operating_hours_start_time: "",
        evening_operating_hours_end_time: "",
        morning_itinerary: { ar: "", en: "" },
        evening_itinerary: { ar: "", en: "" },
        highlights: { ar: "", en: "" },
        long_description: { ar: "", en: "" },
        what_to_bring: { ar: "", en: "" },
        voucher_notes: { ar: "", en: "" },
        free_transfer_areas: { ar: "", en: "" },
        extra_charge_transfer_areas: { ar: "", en: "" },
    };
};

/**
 * ✅ Initialize Tour-specific data
 */
const initializeTourData = (baseFormData, tourData) => {
    const tourType = tourData.type || TOUR_TYPES.DEFAULT;

    return {
        ...baseFormData,
        package_id: tourData.package_id || null,
        type: tourType,
        highlights: parseMultiLang(tourData.highlights),
        long_description: parseMultiLang(tourData.long_description),
        what_to_bring: parseMultiLang(tourData.what_to_bring),
        voucher_notes: parseMultiLang(tourData.voucher_notes),
        free_transfer_areas: parseMultiLang(tourData.free_transfer_areas),
        extra_charge_transfer_areas: parseMultiLang(
            tourData.extra_charge_transfer_areas
        ),

        // Default type fields
        default_itinerary:
            tourType === TOUR_TYPES.DEFAULT
                ? parseMultiLang(tourData.default_itinerary)
                : { ar: "", en: "" },
        operating_hours_start_time:
            tourType === TOUR_TYPES.DEFAULT ? tourData.operating_hours_start_time || "" : "",
        operating_hours_end_time:
            tourType === TOUR_TYPES.DEFAULT ? tourData.operating_hours_end_time || "" : "",

        // Morning & Evening type fields
        morning_operating_hours_start_time:
            tourType === TOUR_TYPES.MORNING_EVENING
                ? tourData.morning_operating_hours_start_time || ""
                : "",
        morning_operating_hours_end_time:
            tourType === TOUR_TYPES.MORNING_EVENING
                ? tourData.morning_operating_hours_end_time || ""
                : "",
        evening_operating_hours_start_time:
            tourType === TOUR_TYPES.MORNING_EVENING
                ? tourData.evening_operating_hours_start_time || ""
                : "",
        evening_operating_hours_end_time:
            tourType === TOUR_TYPES.MORNING_EVENING
                ? tourData.evening_operating_hours_end_time || ""
                : "",
        morning_itinerary:
            tourType === TOUR_TYPES.MORNING_EVENING
                ? parseMultiLang(tourData.morning_itinerary)
                : { ar: "", en: "" },
        evening_itinerary:
            tourType === TOUR_TYPES.MORNING_EVENING
                ? parseMultiLang(tourData.evening_itinerary)
                : { ar: "", en: "" },

        // Set Transfer fields to defaults (not used)
        itinerary: { ar: "", en: "" },
        notes: { ar: "", en: "" },
    };
};
